import React from 'react';
import { useChartTooltip } from './ChartTooltip';

export interface BarSeries {
  label: string;
  color: string;
  data: number[];  // one value per date bucket
}

interface BarChartProps {
  dateLabels: string[];
  series: BarSeries[];
  maxValue: number;
}

/** Round maxValue up to a nice number for the top y-axis tick. */
function niceMax(raw: number): number {
  if (raw <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const normalized = raw / mag;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * mag;
}

const BarChart: React.FC<BarChartProps> = ({ dateLabels, series, maxValue }) => {
  const { show, move, hide, tooltipEl } = useChartTooltip();

  const CHART_H = 220;
  const Y_TICKS = 4;
  const Y_LABEL_W = 36;
  const n = dateLabels.length;

  const top = niceMax(maxValue);
  const tickStep = top / (Y_TICKS - 1);
  const yTicks = Array.from({ length: Y_TICKS }, (_, i) => Math.round(top - i * tickStep));

  const step = n <= 10 ? 1 : n <= 20 ? 2 : n <= 35 ? 4 : 7;

  return (
    <>
      {tooltipEl}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div style={{ display: 'flex', gap: 0 }}>

          {/* Y-axis labels */}
          <div style={{
            width: Y_LABEL_W,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: CHART_H,
            paddingBottom: 1,
          }}>
            {yTicks.map((v, i) => (
              <span key={i} style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--an-text-xs)',
                color: 'var(--an-text-faint)',
                textAlign: 'right',
                lineHeight: 1,
                display: 'block',
                paddingRight: 8,
              }}>
                {v}
              </span>
            ))}
          </div>

          {/* Chart area */}
          <div style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            height: CHART_H,
            borderLeft: '1px solid var(--an-border)',
            borderBottom: '1px solid var(--an-border)',
          }}>

            {/* Horizontal grid lines */}
            {yTicks.slice(0, -1).map((_, i) => {
              const topPct = (i / (Y_TICKS - 1)) * 100;
              return (
                <div key={i} style={{
                  position: 'absolute',
                  left: 0, right: 0,
                  top: `${topPct}%`,
                  height: 1,
                  background: 'var(--an-border-subtle)',
                }} />
              );
            })}

            {/* Bars */}
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'flex-end',
              gap: n > 60 ? 1 : n > 30 ? 2 : 3,
              padding: '0 4px',
            }}>
              {dateLabels.map((label, di) => {
                const total = series.reduce((sum, s) => sum + (s.data[di] ?? 0), 0);
                const colH = top > 0 ? (total / top) * CHART_H : 0;
                const segments = [...series].reverse().filter(s => (s.data[di] ?? 0) > 0);
                const activeSeries = series.filter(s => (s.data[di] ?? 0) > 0);
                return (
                  <div
                    key={di}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      gap: 1,
                      height: '100%',
                      cursor: total > 0 ? 'crosshair' : undefined,
                    }}
                    onMouseEnter={total > 0 ? (e) => show({
                      title: label,
                      subtitle: `${total} ${total === 1 ? 'query' : 'queries'}`,
                      rowsHeader: activeSeries.length > 0 ? 'Version' : undefined,
                      rows: activeSeries.map(s => ({
                        label: s.label,
                        value: s.data[di] ?? 0,
                        color: s.color,
                      })),
                    }, e) : undefined}
                    onMouseMove={total > 0 ? move : undefined}
                    onMouseLeave={total > 0 ? hide : undefined}
                  >
                    {colH > 0 && segments.map((s, si) => {
                      const segH = top > 0 ? ((s.data[di] ?? 0) / top) * CHART_H : 0;
                      return (
                        <div
                          key={si}
                          style={{
                            height: segH,
                            background: s.color,
                            borderRadius: si === segments.length - 1 ? '3px 3px 0 0' : 0,
                          }}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* X-axis labels */}
        <div style={{ position: 'relative', height: 18, marginLeft: Y_LABEL_W, overflow: 'visible' }}>
          {dateLabels.map((d, i) => {
            if (i % step !== 0) return null;
            const pct = n <= 1 ? 0 : (i / (n - 1)) * 100;
            return (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  left: `${pct}%`,
                  transform: 'translateX(-50%)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--an-text-xs)',
                  color: 'var(--an-text-muted)',
                  whiteSpace: 'nowrap',
                  lineHeight: '18px',
                  userSelect: 'none',
                }}
              >
                {d}
              </span>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default BarChart;
